import { Fragment, type ReactNode } from "react";

import { GauInText } from "./GauInText";
import { GauPacksLink } from "./GauPacksLink";

/**
 * Renders a plain string, replacing each whole-word "GAU Packs" mention with a
 * link (default {@link GauPacksLink}) and delegating the surrounding text to
 * {@link GauInText} so any remaining "GAU"/"GAUs" mentions still get the
 * dotted-underline definition tooltip.
 *
 * Use this instead of matching on the full feature copy (e.g.
 * `item === "GAU Packs add-on available"`): the link attaches to the "GAU
 * Packs" token itself, so rewording the surrounding sentence never silently
 * drops the link. Pass `renderLink` when a surface needs its own link styling
 * (e.g. the paywall's themed underline) rather than the default.
 */
export function GauPacksInText({
  children,
  renderLink,
}: {
  children: string;
  renderLink?: () => ReactNode;
}): ReactNode {
  const parts = children.split(/\b(GAU Packs)\b/g);
  return (
    <>
      {parts.map((part, index) => {
        if (part === "GAU Packs") {
          return (
            <Fragment key={index}>
              {renderLink ? renderLink() : <GauPacksLink />}
            </Fragment>
          );
        }
        if (!part) {
          return null;
        }
        return <GauInText key={index}>{part}</GauInText>;
      })}
    </>
  );
}
